import { format, startOfDay } from "date-fns";
import React, { useEffect, useRef, useState } from "react";

import type {
	GetUserCalendarIntegrationStatusResponse,
	MeetingModelJson,
	UpdateMeetingRequestJson,
} from "@performyard/service-contracts";

import { CalendarErrorModal } from "./CalendarErrorModal";
import MeetingForm, { type MeetingFormData } from "./MeetingForm";
import { VideoConferencingErrorModal } from "./VideoConferencingErrorModal";

import { useEditWorkspaceMeeting } from "../../../../../api/hooks";
import useForm from "../../../../../hooks/useForm";
import { useCalendarIntegrationStatus } from "../../hooks";
import { useCalendarErrorHandler, useValidationErrorClearer, useVideoConferencingErrorHandler } from "./hooks";

import { type MeetingFormSchema, meetingFormSchema } from "./meeting-form-schema";
import { type MeetingFormValuesWithOptionalDates, getDefaultFormValues } from "./meeting-form-utils";

const willCalendarEventBeUpdated = (
	meeting: MeetingModelJson | null,
	userCalendarStatus: GetUserCalendarIntegrationStatusResponse | undefined
): boolean => {
	if (!meeting?.calendarEvent || !userCalendarStatus) {
		return false;
	}

	const hasValidCalendarEvent =
		meeting.calendarEvent.eventId && meeting.calendarEvent.provider && meeting.calendarEvent.calendarId;

	if (!hasValidCalendarEvent) {
		return false;
	}

	const isCalendarIntegrationMatched =
		userCalendarStatus.isConnected &&
		userCalendarStatus.provider === meeting.calendarEvent.provider &&
		userCalendarStatus.selectedCalendarId === meeting.calendarEvent.calendarId;

	return isCalendarIntegrationMatched;
};

type EditMeetingModalProps = {
	isOpen: boolean;
	handleClose: () => void;
	meeting: MeetingModelJson | null;
};

export const EditMeetingModal = ({ isOpen, handleClose, meeting }: EditMeetingModalProps): React.ReactElement => {
	const [validationErrors, setValidationErrors] = useState<Partial<Record<keyof MeetingFormSchema, string>>>({});

	const calendarNotificationRef = useRef<string | undefined>(undefined);

	const { userCalendarStatus } = useCalendarIntegrationStatus();
	const { modalState: calendarModalState, handleCalendarError, closeModal, setRetrying } = useCalendarErrorHandler();
	const {
		modalState: vcModalState,
		handleVideoConferencingError,
		closeModal: closeVcModal,
		setRetrying: setVcRetrying,
	} = useVideoConferencingErrorHandler();

	const { mutate: editWorkspaceMeeting, isPending } = useEditWorkspaceMeeting(handleClose, (error, variables) => {
		const context = {
			operation: "update" as const,
			meetingData: variables.data,
			meetingId: variables.meetingId,
		};
		if (handleVideoConferencingError(error, context)) return true;
		return handleCalendarError(error, context);
	});

	const {
		validate,
		updateForm,
		state: formValues,
		setNewDefaultValues,
	} = useForm({
		schema: meetingFormSchema,
		defaultValues: getDefaultFormValues(),
		disableWarning: true,
	});

	const initializedMeetingIdRef = useRef<string | null>(null);

	useEffect(() => {
		if (isOpen && meeting && initializedMeetingIdRef.current !== meeting._id) {
			const startDateTime = new Date(meeting.startDateTime);
			const endDateTime = new Date(meeting.endDateTime);

			setNewDefaultValues({
				name: meeting.name,
				startDate: startOfDay(startDateTime),
				endDate: startOfDay(endDateTime),
				startTime: format(startDateTime, "HH:mm"),
				endTime: format(endDateTime, "HH:mm"),
				description: meeting.description || "",
				carryOver: false,
				addToCalendar: false,
				videoConferencingProvider: null,
			});
			setValidationErrors({});
			initializedMeetingIdRef.current = meeting._id;

			const willUpdate = willCalendarEventBeUpdated(meeting, userCalendarStatus);
			calendarNotificationRef.current = willUpdate
				? "Any updates to this meeting will automatically update in your connected calendar."
				: undefined;
		} else if (!isOpen) {
			initializedMeetingIdRef.current = null;
		}
	}, [isOpen, meeting, userCalendarStatus, setNewDefaultValues]);

	const showCalendarUpdateNotification = willCalendarEventBeUpdated(meeting, userCalendarStatus);
	const calendarNotification = isOpen
		? showCalendarUpdateNotification
			? "Any updates to this meeting will automatically update in your connected calendar."
			: undefined
		: calendarNotificationRef.current;

	// Cast to optional dates type since the form allows clearing dates during editing
	const formValuesWithOptionalDates = formValues as MeetingFormValuesWithOptionalDates;

	useValidationErrorClearer(formValuesWithOptionalDates, validationErrors, setValidationErrors);

	const handleSubmit = (data: MeetingFormData): void => {
		if (!meeting) return;

		const validationResult = validate();
		if (!validationResult.isValid) {
			const errors = Object.entries(validationResult.errors || {}).reduce(
				(acc, [key, error]) => {
					acc[key as keyof MeetingFormSchema] = error.message;
					return acc;
				},
				{} as Partial<Record<keyof MeetingFormSchema, string>>
			);
			setValidationErrors(errors);
			return;
		}

		// Strip form-only fields the BE doesn't accept on update. VC management mirrors
		// calendar — it happens through the row-menu/options dropdown, not this form.
		const { videoConferencingProvider: _drop, ...rest } = data;
		const formattedData: UpdateMeetingRequestJson = {
			...rest,
			startDateTime: data.startDateTime.toISOString(),
			endDateTime: data.endDateTime.toISOString(),
		};

		editWorkspaceMeeting({ meetingId: meeting._id, data: formattedData });
	};

	const onClose = (): void => {
		if (calendarModalState.type !== "none") {
			closeModal();
			return;
		}
		if (vcModalState.type !== "none") {
			closeVcModal();
			return;
		}

		setValidationErrors({});
		handleClose();
	};

	return (
		<>
			{calendarModalState.type === "none" && vcModalState.type === "none" && (
				<MeetingForm
					isOpen={isOpen}
					onClose={onClose}
					onSubmit={handleSubmit}
					formValues={formValuesWithOptionalDates}
					onFormChange={updateForm}
					validationErrors={validationErrors}
					submitLabel="Save Changes"
					isPending={isPending}
					title="Edit Meeting Details"
					showMeetingIntegrations={false}
					calendarUpdateNotification={calendarNotification}
				/>
			)}

			<VideoConferencingErrorModal
				modalState={vcModalState}
				onRetry={() => {
					if (vcModalState.type === "error") {
						setVcRetrying(true);
						const { context } = vcModalState;
						if (context.operation === "update") {
							editWorkspaceMeeting(
								{ meetingId: context.meetingId, data: context.meetingData },
								{
									onSuccess: () => closeVcModal(),
									onError: () => setVcRetrying(false),
								}
							);
						}
					}
				}}
				onContinue={() => {
					if (vcModalState.type === "error") {
						closeVcModal();
					}
				}}
				onClose={closeVcModal}
			/>

			<CalendarErrorModal
				modalState={calendarModalState}
				onRetry={() => {
					if (calendarModalState.type === "error") {
						setRetrying(true);
						const { context } = calendarModalState;
						if (context.operation === "update") {
							editWorkspaceMeeting(
								{ meetingId: context.meetingId, data: context.meetingData },
								{
									onSuccess: () => closeModal(),
									onError: () => setRetrying(false),
								}
							);
						}
					}
				}}
				onContinue={() => {
					if (calendarModalState.type === "error") {
						closeModal();
					}
				}}
				onClose={closeModal}
			/>
		</>
	);
};
